// Pure layout maths for the responsive player.
//
// Everything here is container-driven: the player wrapper and video column
// sizes come from ResizeObservers (see usePlayerLayout.ts), never from the
// window. That keeps the behaviour identical full-page, iframed and fullscreen.

export type Orientation = 'landscape' | 'portrait'
export type TranscriptMode = 'sidebar' | 'overlay'
export type SubtitleSizePreset = 'small' | 'normal' | 'big'

export interface PlayerLayout {
  // measured on the WRAPPER (whole player div):
  wrapperW: number
  wrapperH: number
  orientation: Orientation
  transcriptMode: TranscriptMode
  sidebarWidth: number
  // measured on the VIDEO COLUMN (containerRef):
  columnW: number
  columnH: number
  subtitleBase: number
  isNarrowControls: boolean
}

export const SUBTITLE_SIZE_PRESETS: Record<SubtitleSizePreset, number> = {
  small: 0.8,
  normal: 1,
  big: 1.3,
}

export const SIDEBAR_MIN_WIDTH = 240
export const SIDEBAR_MAX_WIDTH = 400
export const SIDEBAR_WIDTH_RATIO = 0.25

export const SUBTITLE_BASE_MIN = 12
export const SUBTITLE_BASE_MAX = 22
export const SUBTITLE_BASE_REFERENCE_WIDTH = 800
export const SUBTITLE_BASE_AT_REFERENCE = 16

// The one control-button size, at every screen width (decision 7). Gavin's
// stated reason for growing it from 40 was accessibility — "for the hard of
// seeing" — which a size that shrank on phones would defeat. `styles.button`
// and the control row's height both derive from it, so the bar grows with it.
// It is the size whenever the row fits; when it does not, fitControlRow below
// shrinks it, to a 44px floor first (Gavin's zoomed Mac, 11 Sep).
export const CONTROL_BUTTON_SIZE = 56

// Re-measured against the three-zone BBC row (T10), which is a different
// shape from the flat row this number was first taken against.
//
// The left and right zones both carry `flex: 1 1 0`, so they always resolve
// to the SAME width — that is what keeps the skip cluster on the midline.
// It also means the left zone's usable width is fixed by arithmetic rather
// than by what the right zone happens to hold:
//
//   zone width = (columnW - 16 overlay padding - 2x10 row gap - 180 centre) / 2
//
// (centre = three 56px buttons and two 6px gaps.) The left zone's widest
// content is the 3D-audio button, a 6px gap, and the volume container —
// 56 + 6 + (56 + 4 + 70) = 192px. Solving (columnW - 216) / 2 >= 192 gives
// columnW >= 600, so 600 is the last width at which the volume slider still
// fits beside the mute and 3D-audio buttons. Below it the slider goes (phones
// have hardware volume), the row gap tightens to 6px and the two scrub-row
// time labels shrink; the left zone then fits down to a 444px column.
//
// NOTE, and see the Discovered Issues Log: this threshold governs the LEFT
// zone only. The right cluster is now the row's real width constraint and no
// breakpoint here can relieve it — see the log entry for the arithmetic.
// Since 11 Sep `fitControlRow` below owns the row (slider included); this
// width now only shrinks the two scrub-row time labels.
export const NARROW_CONTROLS_WIDTH = 600

// FITTING THE CONTROL ROW — the Discovered Issues Log entry above, resolved.
//
// Gavin, 11 Sep 2026, browser-zoomed on a Mac: the buttons ran over each other
// and View / Follow sat on the total time. Browser zoom is just fewer CSS
// pixels, so a zoomed laptop is a narrow, short player — and the full row needs
// ~916px of column, which a 25%-zoomed laptop with the transcript open does not
// have. Below that the right zone (`flex-end`, `minWidth: 0`) spilled leftward
// across the centre, so controls overlapped rather than vanished.
//
// So the row now gives things up in a fixed order, taking the first step that
// fits and never more than it needs:
//   1. full row: 56px buttons, volume slider, skip ±10, centre on the midline
//   2. drop the volume slider (phones and laptops have hardware volume)
//   3. shrink the buttons, but never below 44px — the WCAG target size, which
//      keeps Gavin's "for the hard of seeing" reason for 56 intact. (Zoomed,
//      a 44 CSS px button is physically larger than a 56 one at 100%.)
//   4. let the centre off the midline: the equal-flex mirroring that holds it
//      there pays for the right zone twice, and on a phone that is the width
//      the row does not have
//   5. drop skip ±10 (the arrow keys still skip). Gavin, 11 Sep evening: "on
//      'grid' the +/- 10 secs gone" — Grid view's narrow column lost them
//      while play still sat on a midline nobody was looking at, so the skips
//      now outlive the centring.
//   6. shrink toward 36px as the last resort
export const MIN_CONTROL_BUTTON_SIZE = 44
export const LAST_RESORT_BUTTON_SIZE = 36
const OVERLAY_PADDING = 16
const ZONE_GAP = 6
const VOLUME_SLIDER_WIDTH = 74 // 4px gap + 70px slider, beside the mute button
const ROW_GAP_WIDE = 10
const ROW_GAP_NARROW = 6

export interface ControlRowContent {
  // Buttons each zone would hold; the verification glyph counts as one.
  leftButtons: number
  rightButtons: number
}

export interface ControlRowFit {
  buttonSize: number
  showVolumeSlider: boolean
  showSkip: boolean
  // true: left and right zones share one width, so play sits on the midline.
  mirrored: boolean
  rowGap: number
}

function zoneWidth(buttons: number, size: number): number {
  return buttons * size + Math.max(buttons - 1, 0) * ZONE_GAP
}

// The width a row needs, at its FULL layout for these switches.
export function controlRowWidth(content: ControlRowContent, fit: Omit<ControlRowFit, 'rowGap'>): number {
  const s = fit.buttonSize
  const left = zoneWidth(content.leftButtons, s) + (fit.showVolumeSlider ? VOLUME_SLIDER_WIDTH : 0)
  const right = zoneWidth(content.rightButtons, s)
  const centre = fit.showSkip ? zoneWidth(3, s) : s
  const gap = fit.showVolumeSlider ? ROW_GAP_WIDE : ROW_GAP_NARROW
  const sides = fit.mirrored ? 2 * Math.max(left, right) : left + right
  return OVERLAY_PADDING + 2 * gap + sides + centre
}

// The largest button size in [min, max] at which the row fits, or null.
function largestFittingSize(
  columnW: number,
  content: ControlRowContent,
  switches: Omit<ControlRowFit, 'rowGap' | 'buttonSize'>,
  min: number,
  max: number,
): number | null {
  // Width is linear in the button size, so solve rather than search — except
  // that max(left, right) can change sides as the size grows, so check the
  // answer and walk back the odd pixel that costs.
  const at = (s: number) => controlRowWidth(content, { ...switches, buttonSize: s })
  if (at(min) > columnW) return null
  const perPx = at(min + 1) - at(min)
  let size = Math.min(max, min + Math.floor((columnW - at(min)) / perPx))
  while (size > min && at(size) > columnW) size--
  return size
}

export function fitControlRow(columnW: number, content: ControlRowContent): ControlRowFit {
  // Each step's `max` is the smallest size the step before it allowed, so the
  // buttons only ever shrink as the column narrows. Without that, giving up
  // the skips or the midline frees enough width to jump back to 56px — and a
  // window being dragged narrower would grow its buttons.
  const steps: Array<{ switches: Omit<ControlRowFit, 'rowGap' | 'buttonSize'>; min: number; max: number }> = [
    { switches: { showVolumeSlider: true, showSkip: true, mirrored: true }, min: CONTROL_BUTTON_SIZE, max: CONTROL_BUTTON_SIZE },
    { switches: { showVolumeSlider: false, showSkip: true, mirrored: true }, min: CONTROL_BUTTON_SIZE, max: CONTROL_BUTTON_SIZE },
    { switches: { showVolumeSlider: false, showSkip: true, mirrored: true }, min: MIN_CONTROL_BUTTON_SIZE, max: CONTROL_BUTTON_SIZE },
    { switches: { showVolumeSlider: false, showSkip: true, mirrored: false }, min: MIN_CONTROL_BUTTON_SIZE, max: MIN_CONTROL_BUTTON_SIZE },
    { switches: { showVolumeSlider: false, showSkip: false, mirrored: false }, min: MIN_CONTROL_BUTTON_SIZE, max: MIN_CONTROL_BUTTON_SIZE },
    { switches: { showVolumeSlider: false, showSkip: false, mirrored: false }, min: LAST_RESORT_BUTTON_SIZE, max: MIN_CONTROL_BUTTON_SIZE },
  ]
  // Before the column is measured there is nothing to fit against; lay out
  // the full row rather than the most cramped one.
  if (!(columnW > 0)) return { ...steps[0].switches, buttonSize: CONTROL_BUTTON_SIZE, rowGap: ROW_GAP_WIDE }
  for (const { switches, min, max } of steps) {
    const size = largestFittingSize(columnW, content, switches, min, max)
    if (size !== null) {
      return { ...switches, buttonSize: size, rowGap: switches.showVolumeSlider ? ROW_GAP_WIDE : ROW_GAP_NARROW }
    }
  }
  // Narrower than anything fits (~330px with a full right zone): the most
  // compact row, and whatever is left over overflows.
  const last = steps[steps.length - 1]
  return { ...last.switches, buttonSize: last.min, rowGap: ROW_GAP_NARROW }
}

// THE TOP-RIGHT CLUSTER against the control bar. The compass sizes off the
// column's height and View / Follow stack under it, and nothing stopped the
// stack running down into the scrub row on a short player. Room is what lies
// between the cluster's top inset and the top of the controls overlay.
// Inset from the player's top and right edges. 16, up from 10: the avatars
// now overhang the plate, and Kai wanted the radar further off the edge (11 Sep).
export const CLUSTER_INSET = 16
const CLUSTER_GAP = 6
const CLUSTER_CLEARANCE = 8 // above the controls overlay
const CAMERA_BUTTON_HEIGHT = 40
const CAMERA_BUTTON_GAP = 2
// Below this a stacked compass is too small to read; put View / Follow beside
// it instead and let the compass have the height. Both are the compass's whole
// footprint — plate plus the avatars overhanging its rim — so 108 is a plate of
// about 87px and 80 one of about 65.
export const MIN_STACKED_COMPASS = 108
export const MIN_COMPASS = 80

export interface ClusterFit {
  direction: 'column' | 'row'
  compassSize: number
}

export function fitTopRightCluster(
  columnH: number,
  controlsHeight: number,
  naturalCompass: number,
  cameraButtons: number,
): ClusterFit {
  if (!(columnH > 0)) return { direction: 'column', compassSize: naturalCompass }
  const room = columnH - controlsHeight - CLUSTER_INSET - CLUSTER_CLEARANCE
  const camera = cameraButtons > 0
    ? CLUSTER_GAP + cameraButtons * CAMERA_BUTTON_HEIGHT + (cameraButtons - 1) * CAMERA_BUTTON_GAP
    : 0
  if (naturalCompass + camera <= room) return { direction: 'column', compassSize: naturalCompass }
  if (room - camera >= MIN_STACKED_COMPASS) return { direction: 'column', compassSize: room - camera }
  return { direction: 'row', compassSize: Math.max(MIN_COMPASS, Math.min(naturalCompass, room)) }
}

function clamp(min: number, value: number, max: number): number {
  if (!Number.isFinite(value)) return min
  return Math.min(Math.max(value, min), max)
}

// A square container counts as portrait — landscape requires strictly wider.
export function getOrientation(w: number, h: number): Orientation {
  return w > h ? 'landscape' : 'portrait'
}

export function getTranscriptMode(orientation: Orientation): TranscriptMode {
  return orientation === 'landscape' ? 'sidebar' : 'overlay'
}

// clamp(240, 25% of the wrapper, 400)
export function getSidebarWidth(wrapperW: number): number {
  return clamp(SIDEBAR_MIN_WIDTH, wrapperW * SIDEBAR_WIDTH_RATIO, SIDEBAR_MAX_WIDTH)
}

// clamp(12, 16 * columnW / 800, 22)
export function getSubtitleBase(videoColumnW: number): number {
  const scaled = (SUBTITLE_BASE_AT_REFERENCE * videoColumnW) / SUBTITLE_BASE_REFERENCE_WIDTH
  return clamp(SUBTITLE_BASE_MIN, scaled, SUBTITLE_BASE_MAX)
}

export function isNarrowControls(videoColumnW: number): boolean {
  return videoColumnW < NARROW_CONTROLS_WIDTH
}

// GRID VIEW WITH THE TRANSCRIPT OPEN — pin the grid right, extend the panel.
//
// Gavin, 9 Sep 2026: "pin it to the right and then keep the transcript and pin
// that up to the start of the grid... we've got a lot of dead space now on the
// grid." The strip of black between the panel and the video is space the
// transcript can have.
//
// The flat surface sizes the video to fill the container's SHORTEST side and
// prorates the other from the video's own aspect ratio (see FlatSurface's
// applyLayout). In a landscape column that makes the rendered width
// `columnH * aspect` — a function of the column's HEIGHT, not its width.
//
// That independence is the whole trick. Every input below is measured on the
// wrapper or on the video, never on the column the panel is about to narrow,
// so widening the panel cannot change the answer and there is no feedback
// loop to settle: the column lands on the video's width in one step and the
// video ends flush against the right edge with no strip left over.
export function gridVideoWidth(columnH: number, videoAspect: number): number {
  if (!Number.isFinite(columnH) || !Number.isFinite(videoAspect)) return 0
  if (columnH <= 0 || videoAspect <= 0) return 0
  return columnH * videoAspect
}

// The transcript column's width in that view: whatever is left once the grid
// has its natural size. Falls back to the ordinary sidebar width — and so
// changes nothing — whenever there is no strip to reclaim:
//   - the video's aspect ratio is not known yet (before `loadedmetadata`),
//   - the column is portrait, where the spare space is above and below the
//     video rather than beside it, and pinning right would not reach it,
//   - the video is already wider than the column, i.e. cropped, which is the
//     usual case for a 2:1 equirectangular master in a squarish column.
// It never returns less than `baseWidth`: this widens the panel or leaves it
// alone, and can never take transcript space away.
export function gridSidebarWidth(
  baseWidth: number,
  wrapperW: number,
  columnH: number,
  videoAspect: number,
): number {
  if (!Number.isFinite(wrapperW) || wrapperW <= 0) return baseWidth
  // The column as it would be WITHOUT this adjustment. Comparing against the
  // measured column instead would re-open the loop the comment above closes.
  const naturalColumnW = wrapperW - baseWidth
  if (naturalColumnW <= 0 || naturalColumnW < columnH) return baseWidth
  const gridW = gridVideoWidth(columnH, videoAspect)
  if (gridW <= 0 || gridW >= naturalColumnW) return baseWidth
  return Math.max(baseWidth, wrapperW - gridW)
}

// Old persisted `forum-subtitle-scale` values were a 0.75–2 multiplier.
export function migrateSubtitleScale(old: number): SubtitleSizePreset {
  if (!Number.isFinite(old)) return 'normal'
  if (old <= 1) return 'small'
  if (old <= 1.5) return 'normal'
  return 'big'
}

// EVERY FEATURE, EVERY WIDTH. Kai, 4 Nov: "all things possible should have a
// UI route at all times on all screen sizes". fitControlRow above may take a
// control off the row; these say where it went instead, so nothing the device
// can do is ever left without a way in. layout.test.ts sweeps them across
// every width.

// Volume: the inline slider when the row has room, else a vertical slider
// popping up from the speaker button. 'hardware' only where a web page cannot
// set the volume at all (iPhone, iPad): there the button mutes.
export type VolumeRoute = 'inline' | 'popup' | 'hardware'
export function volumeRoute(fit: Pick<ControlRowFit, 'showVolumeSlider'>, canSetVolume: boolean): VolumeRoute {
  if (!canSetVolume) return 'hardware'
  return fit.showVolumeSlider ? 'inline' : 'popup'
}

// Skip ±10: the buttons when the row has room; otherwise a double-tap on the
// picture's outer thirds (touch), and the arrow keys (everywhere, always).
export type SkipRoute = 'buttons' | 'double-tap' | 'keys'
export function skipRoute(fit: Pick<ControlRowFit, 'showSkip'>, touch: boolean): SkipRoute {
  if (fit.showSkip) return 'buttons'
  return touch ? 'double-tap' : 'keys'
}
